/**
 * LearnerAdaptiveDetailPage — Phase 8
 * Per-learner adaptive skill health: mastery state, review cards, fix actions.
 * Route: /coach/learners/:learnerId/adaptive
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import CoachLayout from './CoachLayout';
import { getLearnerAdaptiveHealth } from '../../services/api';
import api from '../../services/api';
import { coachAPI } from '../../services/api';

function extractDomainIds(payload) {
  const raw = payload?.domains;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((d) => (typeof d === 'string' ? d : d?.domain)).filter(Boolean);
  if (typeof raw === 'object') return Object.keys(raw);
  return [];
}

const HEALTH_COLORS = {
  good:     { bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.4)',  text: '#4ade80' },
  warning:  { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', text: '#fbbf24' },
  critical: { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  text: '#f87171' },
};

function HealthBadge({ health }) {
  const c = HEALTH_COLORS[health] || HEALTH_COLORS.good;
  return (
    <span
      data-testid="overall-health-badge"
      style={{
        display: 'inline-block',
        padding: '3px 14px',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
        textTransform: 'capitalize',
      }}
    >
      {health}
    </span>
  );
}

export default function LearnerAdaptiveDetailPage() {
  const { learnerId } = useParams();

  const [domain, setDomain]         = useState('');
  const [domains, setDomains]       = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [fixState, setFixState]     = useState({}); // { [skillId]: 'fixing'|'fixed'|'error' }

  // Load coach's domain list
  useEffect(() => {
    coachAPI.listMyDomains()
      .then(({ data }) => {
        const list = extractDomainIds(data);
        setDomains(list);
        if (list.length && !domain) setDomain(list[0]);
      })
      .catch(() => {
        setDomains(['football']);
        setDomain('football');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHealth = useCallback(() => {
    if (!learnerId || !domain) return;
    setLoading(true);
    setError(null);
    getLearnerAdaptiveHealth(learnerId, domain)
      .then(({ data }) => setHealthData(data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load health data'))
      .finally(() => setLoading(false));
  }, [learnerId, domain]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const handleSeedCards = async (skill) => {
    const sid = skill.skill_id;
    setFixState((prev) => ({ ...prev, [sid]: 'fixing' }));
    try {
      const res = await api.post('/coach/adaptive/seed-review-cards', {
        learner_id: learnerId,
        domain,
        skill_id: sid,
      });
      const data = res.data || {};
      if (data.status === 'no_scenes_found') {
        // No published flashcard scenes for this skill → open Content Studio
        setFixState((prev) => ({ ...prev, [sid]: 'no_scenes' }));
      } else {
        setFixState((prev) => ({ ...prev, [sid]: 'fixed' }));
        setTimeout(() => fetchHealth(), 1000);
      }
    } catch {
      setFixState((prev) => ({ ...prev, [sid]: 'error' }));
    }
  };

  const skills = healthData?.skills || [];
  const health = healthData?.overall_health || 'good';

  return (
    <CoachLayout title="Learner Adaptive Detail">
      <div data-testid="learner-adaptive-detail-page">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
              Learner ID
            </div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#cbd5e1' }}>{learnerId}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <select
              className="coach-select"
              style={{ width: 160 }}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            >
              {(domains.length ? domains : ['football']).map((d) => (
                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1).replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button className="btn btn-outline btn-sm" onClick={fetchHealth} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Overall health banner */}
        {healthData && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: 8, padding: '12px 16px', marginBottom: 20,
          }}>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>Overall health:</span>
            <HealthBadge health={health} />
            {healthData.issues?.length > 0 && (
              <span style={{ fontSize: 12, color: '#f87171', marginLeft: 8 }}>
                {healthData.issues.length} issue{healthData.issues.length !== 1 ? 's' : ''} found
              </span>
            )}
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8, padding: '12px 16px', color: '#f87171', marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {loading && (
          <div className="coach-loading">Loading skill health data…</div>
        )}

        {/* Skills table */}
        {!loading && skills.length > 0 && (
          <div className="coach-section" style={{ margin: 0 }}>
            <div className="coach-section-header">
              <h2 className="coach-section-title">Skill Health</h2>
              <span style={{ fontSize: 12, color: '#64748b' }}>{skills.length} skill{skills.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="coach-table" data-testid="skill-health-table">
                <thead>
                  <tr>
                    <th>Skill</th>
                    <th>Mastery</th>
                    <th>Review Cards</th>
                    <th>Last Reviewed</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {skills.map((skill) => {
                    const sid = skill.skill_id;
                    const fixStatus = fixState[sid];
                    const rowBg = fixStatus === 'fixed'
                      ? 'rgba(34,197,94,0.06)'
                      : skill.recommended_action
                        ? 'rgba(239,68,68,0.04)'
                        : undefined;

                    return (
                      <tr key={sid} style={{ background: rowBg, transition: 'background 0.3s' }}>
                        <td style={{ fontWeight: 600, color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12 }}>
                          {sid}
                        </td>
                        <td>
                          {skill.has_mastery ? (
                            <span style={{ color: '#4ade80', fontWeight: 700 }}>✓</span>
                          ) : (
                            <span style={{ color: '#f87171', fontWeight: 700 }}>✗</span>
                          )}
                        </td>
                        <td>
                          {skill.has_review_cards ? (
                            <span style={{ color: '#4ade80' }}>{skill.card_count} card{skill.card_count !== 1 ? 's' : ''}</span>
                          ) : (
                            <span style={{
                              color: '#f87171', fontWeight: 700,
                              background: 'rgba(239,68,68,0.1)', padding: '2px 8px', borderRadius: 4,
                            }}>
                              Missing
                            </span>
                          )}
                        </td>
                        <td style={{ color: '#64748b', fontSize: 12 }}>
                          {skill.last_reviewed
                            ? new Date(skill.last_reviewed).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>
                          {skill.is_overdue && (
                            <span style={{
                              background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)',
                              color: '#fbbf24', borderRadius: 4, fontSize: 11, fontWeight: 700, padding: '2px 8px',
                            }}>
                              Overdue
                            </span>
                          )}
                          {!skill.is_overdue && !skill.recommended_action && (
                            <span style={{ color: '#4ade80', fontSize: 11 }}>OK</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>

                            {/* Missing cards → Seed then fallback to Content Studio */}
                            {skill.recommended_action === 'generate_review_cards' && fixStatus !== 'fixed' && (
                              <>
                                <button
                                  data-testid={`seed-btn-${sid}`}
                                  className="btn btn-primary btn-sm"
                                  style={{ fontSize: 11 }}
                                  disabled={fixStatus === 'fixing'}
                                  onClick={() => handleSeedCards(skill)}
                                  title="Seed review cards from published flashcard scenes"
                                >
                                  {fixStatus === 'fixing' ? 'Seeding…'
                                    : fixStatus === 'error'   ? 'Retry Seed'
                                    : fixStatus === 'no_scenes' ? 'No scenes'
                                    : '＋ Seed Cards'}
                                </button>
                                {/* Always offer Content Studio shortcut for card creation */}
                                <Link
                                  to={`/coach/content-studio?domain=${encodeURIComponent(domain)}&artifact_type=flashcard&prompt=${encodeURIComponent(
                                    `Create 3 spaced-repetition flashcard review cards for the skill "${sid.split('.').pop().replace(/_/g,' ')}" in the "${domain.replace(/_/g,' ')}" domain. Each card must have a clear question, the correct answer, and a brief explanation. Format as flashcard scenes.`
                                  )}`}
                                  style={{ fontSize: 11, color: '#a78bfa', textDecoration: 'none', whiteSpace: 'nowrap' }}
                                  title="Create flashcard content in Content Studio"
                                >
                                  ✨ Create in Studio
                                </Link>
                              </>
                            )}

                            {/* Overdue cards → learner needs to review */}
                            {skill.recommended_action === 'prompt_review' && (
                              <span
                                title="Learner has cards due for review — encourage them to complete their daily review session"
                                style={{ fontSize: 11, color: '#fbbf24', cursor: 'help' }}
                              >
                                📬 Nudge learner to review
                              </span>
                            )}

                            {fixStatus === 'fixed' && (
                              <span style={{ color: '#4ade80', fontSize: 11, fontWeight: 700 }}>✓ Seeded</span>
                            )}

                            {/* Link to Item Bank for this domain+skill */}
                            <Link
                              to={`/coach/workspace/${domain}/item-bank?skill=${encodeURIComponent(sid)}`}
                              style={{ fontSize: 11, color: '#818cf8', textDecoration: 'none', whiteSpace: 'nowrap' }}
                              title={`View item bank for ${sid}`}
                            >
                              🗃 Item Bank
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && skills.length === 0 && domain && (
          <div className="coach-empty">
            <span className="coach-empty-icon">📊</span>
            <div className="coach-empty-title">No skill data yet</div>
            <div className="coach-empty-desc">
              This learner has no adaptive skill records for the <strong>{domain}</strong> domain.
              They need to complete at least one session to generate mastery data.
            </div>
          </div>
        )}
      </div>
    </CoachLayout>
  );
}
